import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import { SUBURBS, SERVICES } from "@/lib/constants";
import { SUBURB_CONTENT, NEARBY } from "@/lib/suburbContent";
import { ArrowRight, MapPin, Building2, CheckCircle2 } from "lucide-react";

function LocalBusinessSchema({ suburb }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Site Cleanup — ${suburb.name}`,
    "description": suburb.metaDesc,
    "areaServed": {
      "@type": "Place",
      "name": `${suburb.name}${suburb.postcode ? " " + suburb.postcode : ""}, VIC, Australia`,
    },
    "priceRange": "Quoted per job",
    "serviceType": ["Site clean-up", "Vacant lot clean-up", "Builder waste removal", "End-of-build tidy-up"],
  };
  // JSON-LD safety: escape characters that could break out of the <script> tag
  // (angle brackets, ampersands, and Unicode line separators that JSON.stringify does not escape by default)
  const safeJson = JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safeJson }} />;
}

function SuburbPage({ slug }) {
  const meta = SUBURBS.find((s) => s.slug === slug);
  const c = SUBURB_CONTENT[slug];
  if (!c) return null;
  const nearby = (NEARBY[slug] || []).map((s) => SUBURBS.find((x) => x.slug === s)).filter(Boolean);

  return (
    <PublicLayout>
      <Seo title={c.metaTitle} description={c.metaDesc} />
      <LocalBusinessSchema suburb={c} />

      {/* HERO */}
      <section className="bg-asphalt text-white py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 rs-diag opacity-25" />
        <div className="rs-container relative">
          <div className="flex items-center gap-2 text-xs text-white/60 uppercase tracking-widest">
            <Link to="/service-areas" className="hover:text-safety">Service Areas</Link>
            <span>/</span>
            <span className="text-white/85">{c.name}{c.postcode && ` ${c.postcode}`}</span>
          </div>
          <span className="rs-eyebrow text-safety mt-6 block">Service area · VIC {c.postcode}</span>
          <h1 className="mt-3 text-5xl md:text-7xl uppercase font-display leading-[0.95] max-w-4xl">{c.heading}</h1>
          <p className="mt-6 max-w-3xl text-white/80 text-base md:text-lg leading-relaxed">{c.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/request-quote" className="rs-btn-primary" data-testid={`suburb-cta-${slug}`}>
              Get my free {c.name} quote <ArrowRight size={16} className="ml-2" />
            </Link>
            <Link to="/services" className="rs-btn-outline-light">Browse all services</Link>
          </div>
        </div>
      </section>

      {/* ESTATES */}
      {(c.estates.length > 0 || (c.nearbyEstates || []).length > 0) && (
        <section className="py-20 bg-white border-b border-border">
          <div className="rs-container">
            <div className="grid md:grid-cols-3 gap-10">
              <div>
                <span className="rs-eyebrow">Estates & developments</span>
                <h2 className="mt-3 text-3xl md:text-4xl uppercase leading-tight">Estates we service in {c.name}</h2>
                <p className="mt-4 text-muted-foreground">If your block is in one of the developments below, we've already handled enquiries in the area and can match a contractor familiar with the estate rules.</p>
              </div>
              <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {c.estates.map((e) => (
                  <div key={e.name} className="rs-card" data-testid={`estate-${slug}-${e.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                    <div className="flex items-start gap-3">
                      <span className="w-9 h-9 grid place-items-center bg-safety/10 text-safety shrink-0"><Building2 size={16} /></span>
                      <div>
                        <p className="font-display uppercase text-lg leading-tight">{e.name}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{e.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {(c.nearbyEstates || []).length > 0 && (
                  <div className="sm:col-span-2 mt-2">
                    <p className="rs-eyebrow">Nearby estates we also cover from {c.name}</p>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {c.nearbyEstates.map((e) => (
                        <div key={e.name} className="border border-dashed border-border p-4 bg-concrete">
                          <p className="font-display uppercase text-base leading-tight">{e.name} <span className="text-xs text-muted-foreground font-sans normal-case tracking-normal">· {e.suburb}</span></p>
                          <p className="mt-1 text-xs text-muted-foreground">{e.note}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* COMMON JOBS */}
      <section className="py-20">
        <div className="rs-container grid md:grid-cols-3 gap-10">
          <div>
            <span className="rs-eyebrow">Common jobs</span>
            <h2 className="mt-3 text-3xl md:text-4xl uppercase leading-tight">Clean-up work in {c.name}</h2>
            <p className="mt-4 text-muted-foreground">These are the jobs we quote on most often in this suburb. If yours isn't listed, send us a photo anyway — chances are we've seen it.</p>
          </div>
          <ul className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {c.jobs.map((j, i) => (
              <li key={i} className="flex gap-3 items-start border-l-2 border-safety pl-4 py-1">
                <CheckCircle2 className="text-safety mt-0.5 shrink-0" size={16} />
                <span className="text-sm text-asphalt">{j}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* LOCAL NOTES */}
      <section className="py-16 bg-asphalt text-white">
        <div className="rs-container grid md:grid-cols-3 gap-10 items-start">
          <div>
            <span className="rs-eyebrow text-safety">Good to know</span>
            <h2 className="mt-3 text-3xl md:text-4xl uppercase leading-tight">What to know about {c.name} clean-ups</h2>
          </div>
          <p className="md:col-span-2 text-white/80 text-base leading-relaxed">{c.localNotes}</p>
        </div>
      </section>

      {/* SERVICES LINKS + NEARBY */}
      <section className="py-20 bg-white">
        <div className="rs-container grid md:grid-cols-2 gap-12">
          <div>
            <span className="rs-eyebrow">Services in {c.name}</span>
            <h2 className="mt-3 text-3xl uppercase font-display">Explore the services we quote on locally</h2>
            <ul className="mt-6 space-y-2 text-sm">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to="/services" className="text-asphalt hover:text-safety flex items-center gap-2">
                    <ArrowRight size={14} className="text-safety" /> {s.title} in {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <span className="rs-eyebrow">Nearby areas</span>
            <h2 className="mt-3 text-3xl uppercase font-display">Also servicing near {c.name}</h2>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {nearby.map((n) => (
                <Link key={n.slug} to={`/service-areas/${n.slug}`} className="border border-border p-4 hover:border-safety transition-colors" data-testid={`nearby-${n.slug}`}>
                  <MapPin size={14} className="text-safety" />
                  <p className="mt-2 font-display uppercase text-sm">{n.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">VIC {n.postcode}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-safety text-white">
        <div className="rs-container grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl md:text-5xl uppercase font-display leading-tight">Free {c.name} clean-up quote — no obligation.</h2>
            <p className="mt-3 text-white/90 max-w-lg">Send a few photos and your street address. We'll come back within 1 business day with a matched local contractor and a written quote.</p>
          </div>
          <div className="md:justify-self-end">
            <Link to="/request-quote" className="inline-flex items-center gap-2 bg-asphalt text-white px-8 py-4 font-semibold uppercase tracking-widest border-2 border-asphalt hover:bg-white hover:text-asphalt transition-colors">
              Get my {c.name} quote <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function IndexPage() {
  return (
    <PublicLayout>
      <Seo
        title="Service Areas | Site Clean-Up Melbourne & Victoria | Site Cleanup"
        description="Site clean-up service areas across Melbourne and Victoria — priority coverage for Epping, Wollert, Craigieburn, Mickleham, Donnybrook, Mernda, South Morang, Tarneit and Clyde."
      />
      <section className="bg-asphalt text-white py-20">
        <div className="rs-container">
          <span className="rs-eyebrow text-safety">Service Areas</span>
          <h1 className="mt-3 text-5xl md:text-7xl uppercase max-w-3xl">Melbourne, Victoria & expanding across Australia.</h1>
          <p className="mt-4 max-w-2xl text-white/75">Priority coverage across Melbourne's northern, western and south-eastern growth corridors — including the biggest new-build postcodes in the state.</p>
        </div>
      </section>
      <section className="py-20">
        <div className="rs-container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SUBURBS.map((s) => {
            const c = SUBURB_CONTENT[s.slug];
            const teaser = c ? c.intro.split(".")[0] + "." : `Vacant lot clean-ups, builder waste removal and end-of-build tidy-ups in ${s.name}.`;
            const estateCount = c?.estates?.length || 0;
            return (
              <Link key={s.slug} to={`/service-areas/${s.slug}`} className="rs-card block" data-testid={`suburb-${s.slug}`}>
                <div className="flex items-baseline justify-between">
                  <span className="rs-eyebrow">{s.state} {s.postcode}</span>
                  {estateCount > 0 && <span className="text-[11px] font-semibold text-safety">{estateCount} estate{estateCount > 1 ? "s" : ""} covered</span>}
                </div>
                <h2 className="font-display text-2xl uppercase mt-2">Site clean-up {s.name}</h2>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{teaser}</p>
                <span className="mt-4 inline-block text-safety font-semibold text-sm">View area →</span>
              </Link>
            );
          })}
        </div>
      </section>
    </PublicLayout>
  );
}

export default function ServiceAreas() {
  const { slug } = useParams();
  if (slug && SUBURB_CONTENT[slug]) return <SuburbPage slug={slug} />;
  return <IndexPage />;
}
